import Badge from '@/app/common/components/Badge'
import { Button } from '@/app/common/components/button/Button'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import Checkbox from '@/app/common/components/Checkbox'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import LoadingCard from '@/app/common/components/LoadingCard'
import MultipleSelector, {
  ListItem,
} from '@/app/common/components/MultipleSelector'
import PageLoader from '@/app/common/components/PageLoader'
import Pagination, { PerPageOption } from '@/app/common/components/Pagination'
import { usePagination } from '@/app/common/usePagination'
import { TABLE_FILTERS } from '@/app/pages/applicants/pre_selected/PreSelectedView'
import {
  AwardDialog,
  ManualAward,
} from '@/app/pages/program_purse_settings/_components/student-management/award-form/award-form.tsx'
import { currencyFormatter } from '@/lib/utils'
import { FamilyApplicationQueries } from '@/queries/family_applications/queries'
import {
  FamilyApplication,
  SHOULD_DISPLAY_PURSE_STATUS,
} from '@/queries/family_applications/requests'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shad/ui/table'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useQuery } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { Award, FilterIcon, Search, Users } from 'lucide-react'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Purse } from '../../schema'

// Simple CardTitle component
const CardTitle = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => <h3 className={`text-lg font-semibold ${className}`}>{children}</h3>

// Simple Avatar components
const Avatar = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => (
  <div
    className={`inline-flex items-center justify-center rounded-full ${className}`}
  >
    {children}
  </div>
)

const AvatarFallback = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => (
  <div
    className={`flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-xs font-medium ${className}`}
  >
    {children}
  </div>
)

interface StudentManagementProps {
  purses: Purse[]
  students?: FamilyApplication[]
}

const STUDENTS_ONLY = 1

export function StudentManagement({ purses }: StudentManagementProps) {
  const [selectedStudents, setSelectedStudents] = useState<ManualAward>([])
  const [showAwardDialog, setShowAwardDialog] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState<ListItem[]>([])
  const {
    currentPage,
    setCurrentPage,
    perPage,
    setPerPage,
    onSearchChange,
    debouncedSearch,
    search,
  } = usePagination()

  const selectedProgram = useSelector(selectSelectedProgram)

  const ApplicationListQuery = useQuery(
    FamilyApplicationQueries.getPaginatedListByProgramId(
      perPage,
      currentPage,
      selectedProgram.id,
      debouncedSearch,
      selectedFilter.map((filter) => filter.id).join(','),
      undefined,
      undefined,
      STUDENTS_ONLY
    )
  )

  function handleAwardChange(newStudentAward: ManualAward[number]) {
    setSelectedStudents((prev) => {
      return prev.map((student) => {
        if (student.studentId === newStudentAward.studentId) {
          return {
            ...student,
            manualAwardAmount: newStudentAward.manualAwardAmount,
            manualAwardReason: newStudentAward.manualAwardReason,
          }
        } else {
          return student
        }
      })
    })
  }

  function handleAwardRemove(studentId: number) {
    setSelectedStudents((prev) => {
      return prev.map((student) => {
        if (student.studentId === studentId) {
          return {
            ...student,
            manualAwardAmount: 0,
            manualAwardReason: '',
          }
        } else {
          return student
        }
      })
    })
  }

  const handleSelectStudent = (studentId: number) => {
    const student = ApplicationListQuery.data?.data.find(
      (s) => s.student_id === studentId
    )
    if (!student) return

    setSelectedStudents((prev) => {
      const isAlreadySelected = prev.some((s) => s.studentId === studentId)
      if (isAlreadySelected) {
        return prev.filter((s) => s.studentId !== studentId)
      }
      return [
        ...prev,
        {
          studentId,
          manualAwardAmount: 0,
          manualAwardReason: '',
        },
      ]
    })
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    setSelectedStudents([])
  }

  const handlePerPageChange = (newItemsPerPage: PerPageOption) => {
    setPerPage(newItemsPerPage)
    setSelectedStudents([])
  }

  const handleFilterChange = (newFilter: React.SetStateAction<ListItem[]>) => {
    const filterValue =
      typeof newFilter === 'function' ? newFilter(selectedFilter) : newFilter
    setSelectedFilter(filterValue)
    setSelectedStudents([])
    setCurrentPage(1) // Reset to first page when filter changes
  }

  const totalAwarded = purses.reduce(
    (sum, purse) => sum + purse.balance_distribuited,
    0
  )

  const handleAwardComplete = () => {
    setSelectedStudents([])
    setShowAwardDialog(false)
  }

  if (showAwardDialog) {
    return (
      <AwardDialog
        familyApplications={ApplicationListQuery.data?.data || []}
        onOpenChange={setShowAwardDialog}
        selectedStudents={selectedStudents}
        purses={purses}
        onAwardChange={handleAwardChange}
        onAwardRemove={handleAwardRemove}
        onAwardComplete={handleAwardComplete}
      />
    )
  }
  return (
    <div className="space-y-6 font-sans">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              <div>
                <div className="text-sm text-gray-600">Total Students</div>
                <div className="text-lg font-semibold text-blue-600">
                  {ApplicationListQuery.data?.total || 0}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-purple-600" />
              <div>
                <div className="text-sm text-gray-600">Total Awarded</div>
                <div className="text-lg font-sans font-semibold text-purple-600">
                  {currencyFormatter.format(totalAwarded)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Student Filters</CardTitle>
            <div className="flex gap-2">
              <Button
                disabled={selectedStudents.length === 0}
                onClick={() => setShowAwardDialog(true)}
                className="gap-2"
              >
                <Award className="h-4 w-4" />
                Award Selected ({selectedStudents.length})
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="space-y-2 max-w-sm">
              <Label htmlFor="search">Search Students</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="search"
                  placeholder="Name, ID, or email..."
                  value={search}
                  onChange={(e) => {
                    setSelectedStudents([])
                    onSearchChange(e.target.value)
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <div className="mb-2">
                <Label>Filter Students</Label>
              </div>
              <MultipleSelector
                list={TABLE_FILTERS.sort((a, b) =>
                  a.name.localeCompare(b.name)
                )}
                selected={selectedFilter}
                setSelected={handleFilterChange}
                icon={<FilterIcon className="text-primary" />}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Student Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>
              Students ({ApplicationListQuery.data?.data?.length || 0} found)
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              Showing{' '}
              {(ApplicationListQuery.data?.data?.length || 0) > 0 ? 1 : 0}-
              {ApplicationListQuery.data?.data?.length || 0} of{' '}
              {ApplicationListQuery.data?.data?.length || 0} filtered results
            </div>
          </div>
        </CardHeader>
        <PageLoader
          customErrorUI={
            <div className="py-10 text-center">
              <div className="text-lg font-semibold">No results found</div>
              <div className="text-sm text-gray-500">
                Please try again with different filters.
              </div>
            </div>
          }
          customLoadingUI={
            <div>
              <LoadingCard
                className="shadow-none border-0 my-10"
                title="Loading"
              />
            </div>
          }
          query={ApplicationListQuery}
        >
          {(data) => {
            // Use the data from PageLoader instead of ApplicationListQuery.data
            const filteredStudents = data.data || []

            return (
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          Award Scholarship
                        </TableHead>
                        <TableHead>Sequence #</TableHead>
                        <TableHead>Student</TableHead>
                        <TableHead>Grade</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Purse(s)</TableHead>
                        <TableHead>Total Awarded</TableHead>
                        <TableHead>Last Award</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredStudents.map((student) => (
                        <TableRow key={student.student_id}>
                          <TableCell>
                            {(student.status === 'Eligible' ||
                              student.status === 'Funded') && (
                              <Checkbox
                                checked={selectedStudents.some(
                                  (s) => s.studentId === student.student_id
                                )}
                                onChange={() =>
                                  handleSelectStudent(student.student_id!)
                                }
                                id={`student-${student.student_id}`}
                                name={`student-${student.student_id}`}
                              />
                            )}
                          </TableCell>
                          <TableCell className="text-sm font-medium text-gray-800 ">
                            {student.student_sequence_number}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="text-xs">
                                  {student.student_first_name
                                    ?.slice(0, 1)
                                    .toUpperCase()}
                                  {student.student_last_name
                                    ?.slice(0, 1)
                                    .toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">
                                  {student?.student_first_name}{' '}
                                  {student?.student_last_name}
                                  <span className="text-xs ml-1 text-gray-500">
                                    ({student.student_id})
                                  </span>
                                </div>

                                <div className="text-xs text-gray-600">
                                  {student.applicant_email}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge semanticColor="Informative">
                              {student.student_grade}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              semanticColor={
                                student.status === 'Awarded'
                                  ? 'Positive'
                                  : 'Neutral'
                              }
                            >
                              {student.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {SHOULD_DISPLAY_PURSE_STATUS.includes(
                              student.status
                            ) &&
                              student.program_purses &&
                              student.program_purses
                                .map((purse) => purse.id)
                                .join(', ')}
                          </TableCell>
                          <TableCell className="font-medium">
                            {student.total_awarded_amount &&
                              currencyFormatter.format(
                                student.total_awarded_amount
                              )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {student.last_awarded_at &&
                              format(
                                parseISO(student.last_awarded_at),
                                'MMM dd, yyyy'
                              )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {(data.data.length || 0) > 0 && (
                  <div className="mt-4">
                    <Pagination
                      itemsPerPage={perPage}
                      currentPage={currentPage}
                      totalRows={data.total || 0}
                      onPageChange={handlePageChange}
                      onPerPageChange={handlePerPageChange}
                    />
                  </div>
                )}

                {filteredStudents.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      No students found matching your criteria.
                    </p>
                  </div>
                )}
              </CardContent>
            )
          }}
        </PageLoader>
      </Card>
    </div>
  )
}
